<template>
  <v-row justify="center">
    <v-col cols="12" class="pt-16">
      <v-container>
        <back-button @click.native="$router.go(-1)"></back-button>
        <h3 class="mb-12 home-header-3">
          Select the date and time<br />
          of your vaccination
        </h3>
        <v-card :loading="slotsLoading" class="mx-auto">
          <template slot="progress">
            <v-progress-linear
              color="#006078"
              indeterminate
            ></v-progress-linear>
          </template>
        </v-card>
        <slots-header
          v-if="!slotsLoading"
          v-bind:dates="availabilityDates"
          @prevDate="onHeaderPrevDate()"
          @nextDate="onHeaderNextDate()"
          class="mb-6"
        >
        </slots-header>
        <v-container v-if="!slotsLoading" class="pa-0 mb-16">
          <div v-if="availabilityFlattened.length">
            <div class="mb-4 py-4 px-8 slots__time--header">MORNING</div>
            <availability-row
              v-if="availabilityInHours(getMorningHours)"
              v-bind:hours="getMorningHours"
              v-bind:availabilityModel="availability"
            ></availability-row>
            <slots-not-available v-else class="mb-4"></slots-not-available>
            <div class="mb-4 py-4 px-8 slots__time--header">AFTERNOON</div>
            <availability-row
              v-if="availabilityInHours(getAfternoonHours)"
              v-bind:hours="getAfternoonHours"
              v-bind:availabilityModel="availability"
            ></availability-row>
            <slots-not-available v-else class="mb-4"></slots-not-available>
            <div class="mb-4 py-4 px-8 slots__time--header">EVENING</div>
            <availability-row
              v-if="availabilityInHours(getEveningHours)"
              v-bind:hours="getEveningHours"
              v-bind:availabilityModel="availability"
            ></availability-row>
            <slots-not-available v-else class="mb-4"></slots-not-available>
          </div>
          <slots-not-available v-else></slots-not-available>
        </v-container>
        <v-alert v-if="showError" color="#CD1041" class="mb-16">
          <div class="px-12 py-10 white--text" v-html="errorMessage"></div>
        </v-alert>
        <div class="text-right">
          <recall-survey v-if="!slotsLoading && accessToken4Patients()"></recall-survey>
          <v-btn
            @click="onAfterContinueClicked"
            v-if="!slotsLoading && getAvailability"
            ref="goToAvailabilityWithinHour"
            color="#006078"
            class="mb-4 mb-lg-0 ml-4 white--text v-btn--primary"
            :disabled="!getAvailabilitySlot"
            :loading="schedulingAppointment"
          >
            Continue
          </v-btn>
        </div>
      </v-container>
    </v-col>
  </v-row>
</template>

<style scoped>
.slots__time--header {
  border-radius: 8px;
  background: rgba(0, 126, 147, 0.05);
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  line-height: 25px;
  letter-spacing: 0.1em;
  color: #007e93;
}
</style>

<script>
import config from '@/config';
import auth4Patients from '@/helpers/auth4Patients';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment/moment';
import BackButton from '@/components/BackButton';
import SlotsHeader from './_Header';
import SlotsNotAvailable from './_SlotsNotAvailable';
import AvailabilityRow from './_AvailabilityRow';
import RecallSurvey from '@/components/RecallSurvey';
import { getSesKey } from '@/helpers/session.ts';
import { calculateAge } from '@/helpers/ageCalculation.ts';

const DAYS_IN_WEEK_DESKTOP = 7;
const DAYS_IN_WEEK_MOBILE = 2;

export default Vue.extend({
  components: {
    BackButton,
    SlotsHeader,
    SlotsNotAvailable,
    AvailabilityRow,
    RecallSurvey
  },
  name: 'BookingSlots',
  data: () => ({
    showError: false,
    errorMessage: '',
    slotsLoading: true,
    schedulingAppointment: false,
    getAvailabilityStartDate: null,
    getAvailabilityStartTime: null,
    availabilityStartDate: null
  }),
  computed: {
    ...mapGetters([
      'getBookingApiURLs',
      'getAge',
      'getAppointmentProcedure',
      'getClinic',
      'getAvailability',
      'getAvailabilitySlot',
      'getSlot',
      'getPatient',
      'getFlowName'
    ]),
    MOBILE() {
      return !this.$vuetify.breakpoint.lgAndUp;
    },
    DAYS_IN_WEEK() {
      return this.MOBILE ? DAYS_IN_WEEK_MOBILE : DAYS_IN_WEEK_DESKTOP;
    },
    availability() {
      const availability = this.getAvailability;
      const availabilityKeys = Object.keys(availability);
      const startDayKey = this.availabilityStartDate.format('YYYY-MM-DD');
      const startDayIndex = availabilityKeys.indexOf(startDayKey);
      const result = {};
      if (startDayIndex === -1) {
        return {};
      }
      for (let d = startDayIndex; d < startDayIndex + this.DAYS_IN_WEEK; d++) {
        const key = availabilityKeys[d];
        if (key in availability) {
          result[key] = availability[key];
        }
      }
      return result;
    },
    availabilityFlattened() {
      const availabilityArr = Object.values(this.availability);
      const slotsArr = availabilityArr.map((a) => (a ? Object.values(a) : []));
      const slots = [].concat(...slotsArr);
      return slots;
    },
    availabilityDates() {
      const dates = Object.keys(this.availability);
      return dates;
    },
    getMinHour() {
      const hours = this.availabilityFlattened.map((a) =>
        moment(a.slot_start, 'HH:mm:ss').format('H')
      );
      return Math.min(...hours);
    },
    getMaxHour() {
      const hours = this.availabilityFlattened.map((a) =>
        moment(a.slot_start, 'HH:mm:ss').format('H')
      );
      return Math.max(...hours);
    },
    getMorningHours() {
      const morningHours = [];
      const maxHour = Math.min(11, this.getMaxHour);
      for (let i = this.getMinHour; i <= maxHour; i++) {
        morningHours.push(i);
      }
      return morningHours;
    },
    getAfternoonHours() {
      const afternoonHours = [];
      const minHour = Math.max(12, this.getMinHour);
      const maxHour = Math.min(17, this.getMaxHour);
      for (let i = minHour; i <= maxHour; i++) {
        afternoonHours.push(i);
      }
      return afternoonHours;
    },
    getEveningHours() {
      const eveningHours = [];
      const minHour = Math.max(18, this.getMinHour);
      for (let i = minHour; i <= this.getMaxHour; i++) {
        eveningHours.push(i);
      }
      return eveningHours;
    },
    getClinicAvailabilityStartDate() {
      return moment(this.getClinic.availability_date);
    },
    getClinicAvailabilityStartTime() {
      return moment(this.getClinic.availability_start, 'HH:mm:ss');
    },
    getSlotsUrl() {
      const age = this.getAge || (this.getPatient.patientDetails && this.getPatient.patientDetails.dateOfBirth ? calculateAge(this.getPatient.patientDetails.dateOfBirth) : 0);
      return (
        this.getBookingApiURLs.getSlotsUrl +
        '&age=' +
        age +
        '&clinicId=' +
        this.getClinic.clinic_id +
        '&procedureId=' +
        this.getAppointmentProcedure.procedureId +
        '&appointmentDate=' +
        this.getAvailabilityStartDate.format('YYYY-MM-DD') +
        '&appointmentDate_Min=' +
        this.getAvailabilityStartDate.format('YYYY-MM-DD') +
        '&appointmentTime_Min=' +
        this.getAvailabilityStartTime.format('HH:mm:ss')
      );
    }
  },
  mounted: function() {
    this.setAvailabilityDates();
    this.getSlots();
  },
  watch: {
    getAvailabilitySlot: function(val) {
      this.$refs.goToAvailabilityWithinHour.$el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  },
  methods: {
    accessToken4Patients() {
      if (!auth4Patients.verifySavedToken()) {
        return false;
      }
      return localStorage.getItem(config.patientAuthTokenName);
    },
    availabilityInHours(hours) {
      const availability = this.availabilityFlattened.filter((s) => {
        const slotHour = moment(s.slot_start, 'HH:mm:ss').format('H');
        if (hours.includes(Number(slotHour))) {
          return true;
        } else {
          return false;
        }
      });
      return availability.length;
    },
    onHeaderPrevDate() {
      this.availabilityStartDate = this.availabilityStartDate
        .clone()
        .subtract(this.DAYS_IN_WEEK, 'days');
    },
    onHeaderNextDate() {
      this.availabilityStartDate = this.availabilityStartDate
        .clone()
        .add(this.DAYS_IN_WEEK, 'days');

      if (this.ifAdditionalSlotsRequired()) {
        this.getNextSlots();
      }
    },
    getNextSlots() {
      this.getAvailabilityStartDate = this.getAvailabilityStartDate
        .clone()
        .add(1, 'weeks')
        .startOf('isoWeek');
      this.getAvailabilityStartTime = moment('00:00:00', 'HH:mm:ss');
      this.getSlots();
    },
    ifAdditionalSlotsRequired() {
      const begIndexKey = this.availabilityStartDate.format('YYYY-MM-DD');
      const begIndex = Object.keys(this.getAvailability).indexOf(begIndexKey);
      if (
        begIndex === -1 ||
        begIndex + this.DAYS_IN_WEEK > Object.keys(this.getAvailability).length
      ) {
        return true;
      } else {
        return false;
      }
    },
    onAfterContinueClicked() {
      this.$gtag.event(`Slot selected` + `${this.getFlowName}`, {})
      this.$router.push('/booking/slots-2').catch(() => {});
    },
    setAvailabilityDates() {
      this.getAvailabilityStartDate = this.getClinicAvailabilityStartDate;
      this.getAvailabilityStartTime = this.getClinicAvailabilityStartTime;
      if (this.getAvailabilitySlot) {
        if (
          this.getAvailabilitySlot.date.isAfter(
            this.getClinicAvailabilityStartDate,
            'day'
          )
        ) {
          this.getAvailabilityStartDate = this.getAvailabilitySlot.date;
          this.getAvailabilityStartTime = moment('00:00:00', 'HH:mm:ss');
        }
      }

      if (this.MOBILE && this.getAvailabilityStartDate.isoWeekday() < 7) {
        this.availabilityStartDate = this.getAvailabilityStartDate.clone();
      } else if (this.MOBILE) {
        this.availabilityStartDate = this.getAvailabilityStartDate
          .clone()
          .isoWeekday(6);
      } else {
        this.availabilityStartDate = this.getAvailabilityStartDate
          .clone()
          .startOf('isoWeek');
      }
    },
    getSlots() {
      this.slotsLoading = true;
      fetch(this.getSlotsUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'Key': getSesKey()
        },
        redirect: 'follow',
        body: ''
      })
        .then((response) => response.json())
        .then((response) => {
          this.slotsLoading = false;
          const mergedAvailability = Object.assign(
            {},
            this.getAvailability || {},
            response.response.viewData.availability
          );
          this.$store.commit('setAvailability', mergedAvailability);
        })
        .catch((e) => {});
    }
  }
});
</script>
